import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    Self,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormsModule,
    NgControl,
    ReactiveFormsModule,
} from '@angular/forms';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {NgTemplateOutlet} from '@angular/common';
import {v4 as uuidv4} from 'uuid';
import {TooltipComponent} from '../tooltip/tooltip.component';
import {CheckboxAreaDirective} from '../checkbox-area.directive';

@Component({
    selector: 'app-checkbox-group',
    templateUrl: './checkbox-group.component.html',
    styleUrls: ['./checkbox-group.component.scss'],
    imports: [
        ReactiveFormsModule,
        FormsModule,
        NgbModule,
        TooltipComponent,
        NgTemplateOutlet,
        CheckboxAreaDirective,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxGroupComponent implements ControlValueAccessor, OnInit {
    @Input() label = '';
    @Input() information: string | undefined;
    @Input() description: string | undefined;
    /**
     * When set, wraps the checkbox in a Bootstrap grid row offset by `labelCols` columns (to line
     * the box up with grouped form fields). ngwebbill passes a value (e.g. 4) to match its legacy
     * app-local component; other apps leave it unset for the plain inline checkbox.
     */
    @Input() labelCols: number | null = null;

    control = new FormControl<boolean>(false);
    id: string = uuidv4();

    private onChange: ((value: boolean | null) => void) | undefined;
    private onTouch: (() => void) | undefined;

    constructor(
        @Self() public ngControl: NgControl,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        ngControl.valueAccessor = this;
    }

    get gridClass(): string {
        return this.labelCols != null ? `offset-${this.labelCols} col-${12 - this.labelCols}` : '';
    }

    ngOnInit(): void {
        // Then using ChangeDetectionStrategy.OnPush the component doesn't notice, when the control is marked as
        // touched, and will therefore not show validation errors. However, Angular doesn't offer a simple way of
        // listening to, when a control is marked as touched, that we can hook into. So we have to reassign the
        // markAsTouched() method in order to call detectChanges().
        if (this.ngControl.control != null) {
            const originalMarkAsTouched = this.ngControl.control.markAsTouched.bind(
                this.ngControl.control,
            );
            const changeDetectorRef = this.changeDetectorRef;
            this.ngControl.control.markAsTouched = function (...args): void {
                originalMarkAsTouched.apply(this, args);
                changeDetectorRef.detectChanges();
            };
        }

        this.control.valueChanges.subscribe(data => {
            this.onTouch?.();
            this.onChange?.(data);
        });
    }

    registerOnChange(fn: (value: boolean | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(_: () => void): void {}

    writeValue(value: boolean): void {
        this.control.setValue(value, {emitEvent: false});
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.control.disable();
        } else {
            this.control.enable();
        }
    }
}
